import { useEffect, useState } from 'react'
import {
  attendanceDeleteHoliday,
  attendanceLoadHolidays,
  attendanceSeedHolidays,
  attendanceSaveHoliday,
  type AttendanceHoliday,
} from '../../lib/apiPhpAdapter'
import './AttendanceOrgAdmin.css'

type Props = {
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  embedded?: boolean
  initialYear?: number
}

/** YYYY-MM-DD → D.M.YYYY (den první). */
function formatHolidayDate(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim())
  if (!m) return ymd
  return `${Number(m[3])}.${Number(m[2])}.${m[1]}`
}

export function AttendanceHolidaysPanel({
  showAlert,
  embedded = false,
  initialYear,
}: Props) {
  const [year, setYear] = useState(initialYear ?? new Date().getFullYear())
  const [rows, setRows] = useState<AttendanceHoliday[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [formDate, setFormDate] = useState('')
  const [formName, setFormName] = useState('')

  const reload = async (y = year) => {
    const res = await attendanceLoadHolidays(y)
    setRows(Array.isArray(res.holidays) ? res.holidays : [])
  }

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    attendanceLoadHolidays(year)
      .then((res) => {
        if (!cancelled) setRows(Array.isArray(res.holidays) ? res.holidays : [])
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          showAlert(e instanceof Error ? e.message : 'Kalendář svátků se nepodařilo načíst', 'error')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [year, showAlert])

  const Wrapper = embedded ? 'div' : 'section'
  const wrapperClass = embedded ? 'att-cat' : 'attendance-panel att-cat'

  return (
    <Wrapper className={wrapperClass}>
      {!embedded ? <h2>Kalendář svátků</h2> : null}
      <p className="att-cat__intro">
        Dny v kalendáři nemají pracovní normu a generování plánu směn je přeskočí. Státní svátky ČR
        lze doplnit tlačítkem; vlastní dny volna přidáte ručně.
      </p>

      <div className="att-leave-form__filters">
        <label>
          Rok
          <input
            type="number"
            min={2000}
            max={2100}
            style={{ width: 88 }}
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10) || year)}
          />
        </label>
        <button
          type="button"
          className="system-button att-cat__btn"
          disabled={busy}
          onClick={() => {
            void (async () => {
              setBusy(true)
              try {
                const r = await attendanceSeedHolidays(year)
                showAlert(
                  `Státní svátky ${year}: doplněno ${r.inserted}, už existovalo ${r.skipped}`,
                  'success',
                )
                await reload()
              } catch (e: unknown) {
                showAlert(e instanceof Error ? e.message : 'Doplnění selhalo', 'error')
              } finally {
                setBusy(false)
              }
            })()
          }}
        >
          Doplnit státní svátky ČR
        </button>
      </div>

      <div className="att-leave-form att-leave-form--adjust">
        <label>
          Datum
          <input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} />
        </label>
        <label className="att-leave-form__grow">
          Název
          <input
            type="text"
            value={formName}
            placeholder="např. Ředitelské volno"
            onChange={(e) => setFormName(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="system-button"
          disabled={busy || !formDate || !formName.trim()}
          onClick={() => {
            void (async () => {
              setBusy(true)
              try {
                await attendanceSaveHoliday({
                  day_date: formDate,
                  name: formName.trim(),
                  kind: 'custom',
                  active: true,
                })
                showAlert('Svátek uložen', 'success')
                setFormName('')
                await reload()
              } catch (e: unknown) {
                showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
              } finally {
                setBusy(false)
              }
            })()
          }}
        >
          Přidat den
        </button>
      </div>

      {loading ? <p className="attendance-muted">Načítání…</p> : null}

      <div className="att-cat__wrap">
        <table className="att-cat__table">
          <thead>
            <tr>
              <th className="att-cat__col-narrow">Datum</th>
              <th className="att-cat__col-fill">Název</th>
              <th className="att-cat__col-narrow">Typ</th>
              <th className="att-cat__col-narrow">Stav</th>
              <th className="att-cat__col-actions" aria-label="Akce" />
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.day_date} className={h.active ? undefined : 'att-cat__row--inactive'}>
                <td className="att-cat__col-narrow">{formatHolidayDate(h.day_date)}</td>
                <td className="att-cat__col-name" title={h.name}>
                  {h.name}
                </td>
                <td className="att-cat__col-narrow">{h.kind === 'custom' ? 'vlastní' : 'státní'}</td>
                <td className="att-cat__col-narrow">{h.active ? 'aktivní' : 'vypnuto'}</td>
                <td className="att-cat__col-actions">
                  <button
                    type="button"
                    className="system-button att-cat__btn"
                    disabled={busy}
                    onClick={() => {
                      void (async () => {
                        setBusy(true)
                        try {
                          await attendanceSaveHoliday({
                            day_date: h.day_date,
                            name: h.name,
                            kind: h.kind,
                            active: !h.active,
                          })
                          await reload()
                        } catch (e: unknown) {
                          showAlert(e instanceof Error ? e.message : 'Změna selhala', 'error')
                        } finally {
                          setBusy(false)
                        }
                      })()
                    }}
                  >
                    {h.active ? 'Vypnout' : 'Zapnout'}
                  </button>
                  <button
                    type="button"
                    className="system-button att-cat__btn att-cat__btn--danger"
                    disabled={busy}
                    onClick={() => {
                      void (async () => {
                        if (!window.confirm(`Smazat ${formatHolidayDate(h.day_date)} — ${h.name}?`)) return
                        setBusy(true)
                        try {
                          await attendanceDeleteHoliday(h.day_date)
                          showAlert('Svátek smazán', 'success')
                          await reload()
                        } catch (e: unknown) {
                          showAlert(e instanceof Error ? e.message : 'Smazání selhalo', 'error')
                        } finally {
                          setBusy(false)
                        }
                      })()
                    }}
                  >
                    Smazat
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading ? (
              <tr>
                <td colSpan={5} className="att-cat__empty">
                  Pro rok {year} zatím žádné svátky. Použijte „Doplnit státní svátky ČR“.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </Wrapper>
  )
}
